import React, { useRef, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import AdminPanelModal from './AdminPanelModal';
import ContactForm from './ContactForm';
import Dashboard from './Dashboard';
import { endpoints } from './caminhoApi';

import {
  Card,
  Input,
  Button,
  Typography,
  message as antdMessage,
  Select,
  Form,
  Spin,
  Alert,
  Badge,
  Row,
  Col,
  ConfigProvider,
  theme,
  Switch,
} from 'antd';
import { SettingOutlined, BulbOutlined, BulbFilled } from '@ant-design/icons';
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Analytics } from '@vercel/analytics/react';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

function AppMain() {
  const [authorized, setAuthorized] = useState(null);
  const [pending, setPending] = useState(false);
  const [requestCount, setRequestCount] = useState(0);
  const [platform, setPlatform] = useState('Computador');
  const [form] = Form.useForm();
  const [adminVisible, setAdminVisible] = useState(false);
  const [characterCheckStatus, setCharacterCheckStatus] = useState(null);
  const [characterInfo, setCharacterInfo] = useState(null);
  const [checkingCharacter, setCheckingCharacter] = useState(false);
  const [darkMode, setDarkMode] = useState(false); // 🌙 Dark mode
  const [serverDown, setServerDown] = useState(false); // ✅ novo estado

  const contactFormRef = useRef();

  const toggleTheme = (checked) => {
    setDarkMode(checked);
    localStorage.setItem('darkMode', checked ? 'true' : 'false');
  };

  useEffect(() => {
    const savedTheme = localStorage.getItem('darkMode');
    if (savedTheme === 'true') {
      setDarkMode(true);
    } else {
      setDarkMode(false);
    }
  }, []);

  const handleOpenContact = () => {
    contactFormRef.current?.openModal();
  };

  useEffect(() => {
    // Detecta se é mobile ou desktop
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobile = /iphone|android|ipad|mobile/.test(userAgent);
    setPlatform(isMobile ? 'Mobile' : 'Computador');

    const token = localStorage.getItem('access_token');

    // Se não houver token, não autorizado
    if (!token) {
      setAuthorized(false);
      return;
    }

    // Tenta validar token no backend
    axios.post(endpoints.checkAccess, { token })
      .then(res => {
        if (res.data.authorized) {
          setAuthorized(true);
        } else {
          if (res.data.status === 'request') {
            setPending(true);
          }
          setAuthorized(false);
        }
      })
      .catch(err => {
        console.error("❌ Erro ao conectar no backend:", err.message);
        // Se o backend não responde, marca servidor fora e garante authorized = false
        setServerDown(true);
        setAuthorized(false);

        // Recarrega a página a cada 10 segundos enquanto o servidor estiver offline
      const interval = setInterval(() => {
        window.location.reload();
      }, 300_000);

      return () => clearInterval(interval);
      });
  }, []);

  useEffect(() => {
    loadRequestCount();
  }, []);

  const loadRequestCount = () => {
    axios.get(endpoints.requestCount)
      .then(res => {
        setRequestCount(res.data.count || 0);
      })
      .catch(() => setRequestCount(0));
  };

  const handleRequestAccess = async (values) => {
    const userAgent = navigator.userAgent.toLowerCase();
    const payload = {
      ...values,
      platform,
      userAgent,
      characterName: characterInfo?.name || '',
      characterVocation: characterInfo?.vocation || '',
      characterLevel: characterInfo?.level || ''
    };

    try {
      const res = await axios.post(endpoints.requestAccess, payload);
      localStorage.setItem('access_token', res.data.token);
      antdMessage.success("Solicitação enviada com sucesso. Aguarde aprovação.");
      setTimeout(() => {
        window.location.reload();
      }, 1500); // 1.5 segundos é suficiente para o usuário ver a mensagem
    } catch (error) {
      antdMessage.error("Erro ao enviar solicitação.");
    }
  };

  const handleCharacterCheck = async (name) => {
    setCheckingCharacter(true);
    try {
      const res = await axios.get(`https://api.tibiadata.com/v4/character/${name}`);
      const characterData = res?.data?.character?.character;

      if (res.status === 502 && res?.data?.error_code === 20001) {
        setCharacterCheckStatus('error');
        setCharacterInfo(null);
        form.setFieldValue('character', '');
        form.validateFields(['character']);
        antdMessage.warning('Personagem não encontrado.');
      } else if (characterData?.name) {
        const exists = characterData.name.trim().toLowerCase() === name.trim().toLowerCase();
        if (exists) {
          setCharacterCheckStatus('success');
          setCharacterInfo({
            name: characterData.name,
            vocation: characterData.vocation,
            level: characterData.level
          });
          form.setFieldValue('character', characterData.name);
        } else {
          setCharacterCheckStatus('warning');
          setCharacterInfo(null);
          form.setFieldValue('character', '');
          antdMessage.warning(`Verifique a ortografia. Encontrado: ${characterData.name}`);
        }
      } else {
        setCharacterCheckStatus('error');
        setCharacterInfo(null);
        form.setFieldValue('character', '');
        antdMessage.warning('Personagem não encontrado.');
      }
    } catch (error) {
      setCharacterCheckStatus('error');
      setCharacterInfo(null);
      form.setFieldValue('character', '');
      antdMessage.error('Erro ao verificar personagem.');
    } finally {
      setCheckingCharacter(false);
    }
  };

  if (authorized === null) {
    return (
      <ConfigProvider theme={{ algorithm: darkMode ? theme.darkAlgorithm : theme.defaultAlgorithm }}>
        <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin size="large" />
        </div>
      </ConfigProvider>
    );
  }

  if (serverDown) {
  return (
    <ConfigProvider theme={{ algorithm: darkMode ? theme.darkAlgorithm : theme.defaultAlgorithm }}>
      <div
        style={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: '0 20px',
          background: darkMode ? '#121212' : '#f0f2f5',
          color: darkMode ? '#fff' : '#000',
        }}
      >
        <div
          style={{
            maxWidth: 600,
            width: '100%',
            padding: '40px 30px',
            borderRadius: 16,
            background: darkMode ? '#1e1e1e' : '#fff',
            boxShadow: darkMode ? '0 8px 24px rgba(0,0,0,0.6)' : '0 8px 24px rgba(0,0,0,0.15)',
            border: darkMode ? '1px solid #333' : '1px solid #e0e0e0',
            transition: 'all 0.3s ease',
          }}
        >
          <div style={{ fontSize: 64, marginBottom: 24, color: darkMode ? '#ff6b6b' : '#ff4d4f' }}>
            🚧
          </div>
          <Title level={2} style={{ color: darkMode ? '#fff' : '#111', marginBottom: 16 }}>
            Servidor temporalmente indisponible
          </Title>
          <Text style={{ display: 'block', marginBottom: 32, color: darkMode ? '#aaa' : '#555', lineHeight: 1.8, fontSize: 16 }}>
            Nuestro servidor no está disponible en este momento. Por favor, inténtelo de nuevo más tarde.
          </Text>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
            <Button
              type="primary"
              size="large"
              style={{ minWidth: 140 }}
              //onClick={() => window.location.href = "https://status.koyeb.com/"}
              onClick={() => window.open("https://status.koyeb.com/", "_blank")}
            >
              Ver estado
            </Button>
            <Button
              type="default"
              size="large"
              style={{ minWidth: 140 }}
              onClick={() => window.location.reload()}
            >
              Recargar página
            </Button>
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
}

  if (!authorized) {
    return (
      <ConfigProvider theme={{ algorithm: darkMode ? theme.darkAlgorithm : theme.defaultAlgorithm }}>
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: darkMode ? '#141414' : '#f0f2f5' }}>
          {/* Botão de Tema */}
          <div style={{ position: 'fixed', top: 20, left: 20, zIndex: 1000 }}>
            
          </div>

          {/* Botão Admin */}
          <div style={{ position: 'fixed', top: 10, right: 10, zIndex: 1000 }}>
            <Switch
              size="large"
              checked={darkMode}
              onChange={toggleTheme}
              checkedChildren={<BulbFilled />}
              unCheckedChildren={<BulbOutlined />}
              title="Alternar tema"
              style={{ fontSize: '16px', marginRight: 5 }}
            />
            <Badge count={requestCount} size="small">
              <Button
                type="primary"
                shape="circle"
                size="medium"
                icon={<SettingOutlined />}
                onClick={() => setAdminVisible(true)}
                title="Painel de Administração"
              />
            </Badge>
          </div>

          <AdminPanelModal 
            visible={adminVisible} 
            onClose={() => {
              setAdminVisible(false);
              loadRequestCount();
            }} 
          />

          <ContactForm ref={contactFormRef} />

          <Row justify="center" align="middle" style={{ flex: 1, padding: '60px 20px' }}>
            <Col xs={24} md={10} style={{ height: '100%' }}>
              <div
                style={{
                  height: '100%',
                  borderRadius: 12,
                  padding: 30,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                <img
                  src="/TibiaLogo.png"
                  alt="Tibia Logo"
                  style={{ maxWidth: '90%', marginBottom: 25 }}
                />

                <Title level={2} style={{ marginBottom: 10, fontWeight: 700 }}>
                  Tibia Guild Monitor
                </Title>

                <Text
                  type="secondary"
                  style={{ marginBottom: 30, fontSize: 16, lineHeight: 1.5 }}
                >
                  Área privada y restringida para miembros autorizados.
                </Text>

                <Text type="secondary" style={{ fontSize: 14 }}>
                  Dúvidas?{' '}
                  <button
                    onClick={handleOpenContact}
                    style={{
                      cursor: 'pointer',
                      fontWeight: 600,
                      color: '#1890ff',
                      border: 'none',
                      background: 'transparent',
                      padding: 0,
                      fontSize: 'inherit',
                      fontFamily: 'inherit',
                    }}
                  >
                    Entre em contato com o desenvolvedor
                  </button>
                </Text>

              </div>
            </Col>

            <Col xs={24} md={10}>
              <Card style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <Title level={3} style={{ marginBottom: 24 }}>Registro de Acesso</Title>

                {pending && (
                <Alert
                  message="Solicitação pendente"
                  description={
                    <div>
                      Su solicitud todavía está en análisis. Póngase en contacto con los líderes de la guild para agilizar el proceso.
                      <br />
                      Si su solicitud ha sido aprobada,{' '}
                      <button
                        onClick={() => window.location.reload()}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          margin: 0,
                          color: '#1890ff',
                          cursor: 'pointer',
                          fontWeight: 600,
                          textDecoration: 'underline',
                          fontSize: 'inherit',
                          fontFamily: 'inherit',
                        }}
                      >
                        haga clic aquí para recargar la página
                      </button>
                      .
                    </div>
                  }
                  type="info"
                  showIcon
                  style={{ marginBottom: 24 }}
                />
                )}

                <Form layout="vertical" form={form} onFinish={handleRequestAccess}>
                  <Form.Item
                    label="Su nombre"
                    name="name"
                    rules={[{ required: true, message: 'Introduce tu nombre' }]}
                  >
                    <Input placeholder="Introduce tu nombre" disabled={pending} />
                  </Form.Item>

                  <Form.Item
                    label="Character"
                    name="character"
                    rules={[{ required: true, message: 'Introduce el nombre del personaje' }]}
                    validateStatus={characterCheckStatus}
                    help={
                      characterInfo ? (
                        <div>
                          <Text type="secondary">
                            {characterInfo.name} • {characterInfo.vocation} • Level {characterInfo.level}
                          </Text>
                        </div>
                      ) : null
                    }
                  >
                    <Input
                      placeholder="Nombre del personaje"
                      onBlur={(e) => handleCharacterCheck(e.target.value)}
                      onPressEnter={(e) => handleCharacterCheck(e.target.value)}
                      disabled={pending}
                      suffix={checkingCharacter && <Spin size="small" />}
                    />
                  </Form.Item>

                  <Form.Item
                    label="World"
                    name="world"
                    rules={[{ required: true, message: 'Selecione o mundo' }]}
                  >
                    <Select placeholder="Elige el mundo" disabled={pending}>
                      <Option value="Solidera">Solidera</Option>
                      <Option value="Havera">Havera</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label="Mensaje"
                    name="message"
                    rules={[{ required: true, message: 'Introduce un mensaje' }]}
                  >
                    <TextArea placeholder="Información adicional" rows={2} disabled={pending} />
                  </Form.Item>

                  <Form.Item label="Plataforma" style={{ visibility: 'hidden', position: 'absolute', height: 0 }}>
                    <Input value={platform} disabled />
                  </Form.Item>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      block
                      disabled={pending || checkingCharacter}
                    >
                      Enviar solicitação
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            </Col>
          </Row>

          <footer
            style={{
              textAlign: 'center',
              padding: 16,
              background: darkMode ? '#1f1f1f' : '#fff',
              borderTop: '1px solid #f0f0f0',
              color: darkMode ? '#eee' : '#000'  // cor clara no dark, escura no light
            }}
          >
            © 2025 - Tibia Guild Monitor
          </footer>

        </div>
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider theme={{ algorithm: darkMode ? theme.darkAlgorithm : theme.defaultAlgorithm }}>
      <Dashboard />
    </ConfigProvider>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<AppMain />} />
        <Route path="/admin" element={<AdminPanelModal />} />
      </Routes>
      <SpeedInsights />
      <Analytics />
    </Router>
  );
}

export default App;
